import { EditCommentRequest, EditCommentResponse } from "api/checklists/editComment";
import baseApi from ".";
import createQueryFn from "../createQueryFn";
import api from "api";
import { DeleteCommentResponse, DeleteCommentRequest } from "api/checklists/deleteComment";
import {
  AddInternalCommentRequest,
  AddInternalCommentResponse,
} from "api/checklists/addInternalComment";
import { DeleteChecklistResponse, DeleteChecklistRequest } from "api/checklists/deleteChecklist";
import { EditChecklistResponse, EditChecklistRequest } from "api/checklists/editChecklist";
import { GetChecklistResponse, GetChecklistRequest } from "api/checklists/getChecklist";
import { MoveChecklistResponse, MoveChecklistRequest } from "api/checklists/moveChecklist";
import {
  SetDocumentationDoneResponse,
  SetDocumentationDoneRequest,
} from "api/checklists/setDocumentationDone";
import {
  ExcludePhotosFromPdfResponse,
  ExcludePhotosFromPdfRequest,
} from "api/bin/excludePhotosFromPdf";
import {
  AssignOrderSerialRequest,
  AssignOrderSerialResponse,
} from "api/checklists/assignOrderSerial";
import {
  CreateChecklistSerialRequest,
  CreateChecklistSerialResponse,
} from "api/checklists/createChecklistSerial";
import {
  UnassignOrderSerialResponse,
  UnassignOrderSerialRequest,
} from "api/checklists/unassignOrderSerial";
import { EditOrderSerialResponse, EditOrderSerialRequest } from "api/checklists/editOrderSerial";
import {
  CreateSerialEntityResponse,
  CreateSerialEntityRequest,
} from "api/checklists/createSerialEntity";
import { EditSerialEntityResponse, EditSerialEntityRequest } from "api/checklists/editSerialEntity";
import {
  DeleteSerialEntityRequest,
  DeleteSerialEntityResponse,
} from "api/checklists/deleteSerialEntity";
import {
  AdminSetFunctionCheckRequest,
  AdminSetFunctionCheckResponse,
} from "api/checklists/adminSetFunctionCheck";
import { MoveOrderSerialResponse, MoveOrderSerialRequest } from "api/checklists/moveOrderSerial";
import { MoveSerialEntityResponse, MoveSerialEntityRequest } from "api/checklists/moveSerialEntity";
import { GetChecklistCommentsRequest, GetChecklistCommentsResponse } from "api/checklists/getChecklistComments";

const checklistsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ["Checklist"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      assignOrderSerial: builder.mutation<
        AssignOrderSerialResponse,
        AssignOrderSerialRequest & { orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.assignOrderSerial),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
          { type: "OrderSerial", id: "LIST" },
        ],
      }),

      createChecklistSerial: builder.mutation<
        CreateChecklistSerialResponse,
        CreateChecklistSerialRequest & { orderId: string }
      >({
        queryFn: createQueryFn(({ orderId, ...data }) => api.checklists.createChecklistSerial(data)),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
          { type: "OrderSerial", id: "LIST" },
        ],
      }),

      unassignOrderSerial: builder.mutation<
        UnassignOrderSerialResponse,
        UnassignOrderSerialRequest & { checklistId: string; orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.unassignOrderSerial),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
          { type: "OrderSerial", id: "LIST" },
        ],
      }),

      editOrderSerial: builder.mutation<
        EditOrderSerialResponse,
        EditOrderSerialRequest & { orderId: string; checklistId?: string }
      >({
        queryFn: createQueryFn(api.checklists.editOrderSerial),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
          { type: "OrderSerial", id: "LIST" },
        ],
      }),

      createInternalComment: builder.mutation<
        AddInternalCommentResponse,
        AddInternalCommentRequest
      >({
        queryFn: createQueryFn(api.checklists.addInternalComment),
        invalidatesTags: (result, error, { listId }) => [
          { type: "Checklist", id: listId },
          { type: "ChecklistComment", id: "LIST" }
        ],
      }),

      deleteInternalComment: builder.mutation<
        DeleteCommentResponse,
        DeleteCommentRequest & { checklistId: string }
      >({
        queryFn: createQueryFn(api.checklists.deleteComment),
        invalidatesTags: (result, error, { checklistId }) => [
          { type: "Checklist", id: checklistId },
          { type: "ChecklistComment", id: "LIST" },
        ],
      }),

      editInternalComment: builder.mutation<
        EditCommentResponse,
        EditCommentRequest & { checklistId: string }
      >({
        queryFn: createQueryFn(api.checklists.editComment),
        invalidatesTags: (result, error, { checklistId }) => [
          { type: "Checklist", id: checklistId },
          { type: "ChecklistComment", id: "LIST" },
        ],
      }),

      getChecklist: builder.query<GetChecklistResponse, GetChecklistRequest>({
        queryFn: createQueryFn(api.checklists.getChecklist),
        providesTags: (result, error, checklistId) => [{ type: "Checklist", id: checklistId }],
      }),

      moveChecklist: builder.mutation<MoveChecklistResponse, MoveChecklistRequest>({
        queryFn: createQueryFn(api.checklists.moveChecklist),
        invalidatesTags: (result, error, { checklistId, newOrderId }) => [
          { type: "Checklist", id: checklistId },
          { type: "Order", id: newOrderId },
        ],
      }),

      deleteChecklist: builder.mutation<
        DeleteChecklistResponse,
        DeleteChecklistRequest & { orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.deleteChecklist),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Checklist", id: checklistId },
          { type: "Order", id: orderId },
        ],
      }),

      setDocumentationDone: builder.mutation<
        SetDocumentationDoneResponse,
        SetDocumentationDoneRequest
      >({
        queryFn: createQueryFn(api.checklists.setDocumentationDone),
        invalidatesTags: (result, error, { checklistId }) => [
          { type: "Checklist", id: checklistId },
        ],
      }),

      editChecklist: builder.mutation<EditChecklistResponse, EditChecklistRequest>({
        queryFn: createQueryFn(api.checklists.editChecklist),
        invalidatesTags: (result, error, { id }) => [
          { type: "Order", id: result?.partOfOrder },
          { type: "Checklist", id },
        ],
      }),

      excludePhotosFromPdf: builder.mutation<
        ExcludePhotosFromPdfResponse,
        ExcludePhotosFromPdfRequest & { checklistId: string }
      >({
        queryFn: createQueryFn(api.bin.excludePhotosFromPdf),
        invalidatesTags: (result, error, { checklistId }) => [
          { type: "Checklist", id: checklistId },
        ],
      }),

      createSerialEntity: builder.mutation<
        CreateSerialEntityResponse,
        CreateSerialEntityRequest & { orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.createSerialEntity),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
          { type: "SerialEntity", id: "LIST" },
        ],
      }),

      deleteSerialEntity: builder.mutation<
        DeleteSerialEntityResponse,
        DeleteSerialEntityRequest & { checklistId: string; orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.deleteSerialEntity),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
          { type: "SerialEntity", id: "LIST" },
        ],
      }),

      editSerialEntity: builder.mutation<
        EditSerialEntityResponse,
        EditSerialEntityRequest & { checklistId: string; orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.editSerialEntity),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
          { type: "SerialEntity", id: "LIST" },
        ],
      }),

      editFunctionCheck: builder.mutation<
        AdminSetFunctionCheckResponse,
        AdminSetFunctionCheckRequest
      >({
        queryFn: createQueryFn(api.checklists.adminSetFunctionCheck),
        invalidatesTags: (result, error, { id }) => [{ type: "Checklist", id }],
      }),

      moveOrderSerial: builder.mutation<
        MoveOrderSerialResponse,
        MoveOrderSerialRequest & { orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.moveOrderSerial),
        invalidatesTags: (
          result,
          error,
          { serialId, originalChecklistId, targetChecklistId, orderId }
        ) => [
            { type: "Order", id: orderId },
            { type: "Checklist", id: originalChecklistId },
            { type: "Checklist", id: targetChecklistId },
            { type: "OrderSerial", id: serialId },
            { type: "OrderSerial", id: "LIST" },
          ],
      }),

      moveSerialEntity: builder.mutation<
        MoveSerialEntityResponse,
        MoveSerialEntityRequest & { orderId: string }
      >({
        queryFn: createQueryFn(api.checklists.moveSerialEntity),
        invalidatesTags: (
          result,
          error,
          { serialId, originalChecklistId, targetChecklistId, orderId }
        ) => [
            { type: "Order", id: orderId },
            { type: "Checklist", id: originalChecklistId },
            { type: "Checklist", id: targetChecklistId },
            { type: "SerialEntity", id: serialId },
            { type: "SerialEntity", id: "LIST" },
          ],
      }),

      getChecklistComments: builder.query<
        GetChecklistCommentsResponse,
        GetChecklistCommentsRequest
      >({
        queryFn: createQueryFn(api.checklists.getChecklistComments),
        providesTags: (result, error, { checklistId }) => [
          { type: "ChecklistComment", id: "LIST" },
        ],
      })
    }),
  });

export const {
  useAssignOrderSerialMutation,
  useCreateChecklistSerialMutation,
  useUnassignOrderSerialMutation,
  useEditOrderSerialMutation,
  useCreateInternalCommentMutation,
  useDeleteInternalCommentMutation,
  useEditInternalCommentMutation,
  useGetChecklistQuery,
  useMoveChecklistMutation,
  useDeleteChecklistMutation,
  useSetDocumentationDoneMutation,
  useEditChecklistMutation,
  useExcludePhotosFromPdfMutation,
  useCreateSerialEntityMutation,
  useDeleteSerialEntityMutation,
  useEditSerialEntityMutation,
  useEditFunctionCheckMutation,
  useMoveOrderSerialMutation,
  useMoveSerialEntityMutation,
  useGetChecklistCommentsQuery,
} = checklistsApi;

export default checklistsApi;
